import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem, IconButton, CircularProgress, InputAdornment, Pagination
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, FilterList as FilterListIcon, Flag as FlagIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import MainLayout from '../layouts/MainLayout';
import { useForm, Controller } from 'react-hook-form';
import { metaColaboradorService, MetaColaborador, CreateMetaDTO, UpdateMetaDTO, MesCompetencia, ListarMetasFiltros } from '../services/metaColaboradorService';
import { colaboradorService, Colaborador } from '../services/colaboradorService';
import { ramoService} from '../services/ramoService';
import { Ramo } from '../types/ramo';
import { formatarMoeda } from '../utils/formatters';

const GerenciarMetas: React.FC = () => {
  const [metas, setMetas] = useState<MetaColaborador[]>([]);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [loading, setLoading] = useState(true); // Inicia como true para a busca inicial

  // Filtros
  const [filtroColaborador, setFiltroColaborador] = useState<number | ''>('');
  const [filtroRamo, setFiltroRamo] = useState<number | ''>('');
  const [filtroAno, setFiltroAno] = useState<string>(new Date().getFullYear().toString());
  const [filtroMes, setFiltroMes] = useState<MesCompetencia | ''>((Object.keys(MesCompetencia)[new Date().getMonth()]) as MesCompetencia);

  // Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [metaAtual, setMetaAtual] = useState<MetaColaborador | null>(null);

  // Paginação
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const {
    control,
    register,
    handleSubmit: handleSubmitForm,
    reset,
    formState: { errors },
  } = useForm<CreateMetaDTO>({
    defaultValues: {
      ano: new Date().getFullYear().toString(),
      mes: (Object.keys(MesCompetencia)[new Date().getMonth()]) as MesCompetencia,
      valorMeta: 0,
    },
  });

  // Ref para o debounce da busca
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const carregarDependencias = async () => {
    try {
      const [colabs, ramosData] = await Promise.all([
        colaboradorService.listar(),
        ramoService.listarRamos(),
      ]);
      setColaboradores(colabs);
      setRamos(ramosData);
    } catch (error) {
      toast.error('Erro ao carregar colaboradores ou ramos.');
      console.error(error);
    }
  };

  useEffect(() => {
    carregarDependencias();
    // A busca inicial será acionada pelo useEffect de debounce
  }, []);

  const handleBuscarMetas = useCallback(async () => {
    setLoading(true);
    try {
      const filtros: ListarMetasFiltros = {
        colaboradorId: filtroColaborador,
        ramoId: filtroRamo,
        ano: filtroAno,
        mes: filtroMes,
        page,
        limit,
      };
      const data = await metaColaboradorService.listar(filtros);
      setMetas(data.data);
      setTotal(data.total);
    } catch (error) {
      toast.error('Erro ao buscar metas.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filtroColaborador, filtroRamo, filtroAno, filtroMes, page, limit]);

  // Efeito para busca automática com debounce
  useEffect(() => {
    // Limpa o timeout anterior a cada mudança nos filtros
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Se os filtros mudarem, volta para a primeira página
    if (page !== 1) {
      setPage(1);
      return; // A mudança de página vai disparar o outro useEffect
    }

    // Define um novo timeout para executar a busca após 500ms
    debounceTimeout.current = setTimeout(() => {
      handleBuscarMetas();
    }, 500);
  }, [filtroColaborador, filtroRamo, filtroAno, filtroMes]); // Removido handleBuscarMetas para evitar loop

  const handleOpenDialog = (meta?: MetaColaborador) => {
    if (meta) {
      setMetaAtual(meta);
      reset({
        colaboradorId: meta.colaborador.id,
        ramoId: meta.ramo.id,
        ano: meta.ano,
        mes: meta.mes,
        valorMeta: meta.valorMeta,
      });
    } else {
      setMetaAtual(null);
      reset({
        colaboradorId: filtroColaborador || 0, // Preenche com o filtro se houver
        ramoId: 0,
        ano: filtroAno || new Date().getFullYear().toString(),
        mes: filtroMes || (Object.keys(MesCompetencia)[new Date().getMonth()]) as MesCompetencia,
        valorMeta: 0,
      });
    }
    setOpenDialog(true);
  };

  // Efeito para buscar quando a página muda
  useEffect(() => {
    handleBuscarMetas();
  }, [page, limit, handleBuscarMetas]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const onSubmit = async (data: CreateMetaDTO) => {
    try {
      if (metaAtual) {
        const updateData: UpdateMetaDTO = { valorMeta: data.valorMeta };
        await metaColaboradorService.atualizar(metaAtual.id, updateData);
        toast.success('Meta atualizada com sucesso!');
      } else {
        if (!data.colaboradorId || !data.ramoId) {
          toast.error('Colaborador e Ramo são obrigatórios.');
          return;
        }
        const createData: CreateMetaDTO = {
          ...data,
          valorMeta: data.valorMeta,
        };
        await metaColaboradorService.criar(createData);
        toast.success('Meta criada com sucesso!');
      }
      handleCloseDialog();
      handleBuscarMetas();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Erro ao salvar meta.';
      toast.error(errorMessage);
      console.error('Erro ao salvar meta:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta meta?')) {
      try {
        await metaColaboradorService.excluir(id);
        toast.success('Meta excluída com sucesso!');
        handleBuscarMetas();
      } catch (error) {
        toast.error('Erro ao excluir meta.');
        console.error('Erro ao excluir meta:', error);
      }
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FlagIcon  sx={{ fontSize: '2.2rem' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 0 }}>
              Gerenciamento de Metas
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Nova Meta
          </Button>
        </Box>

        {/* Seção de Filtros */}
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterListIcon color="action" />
            <Typography variant="h6" sx={{ ml: 1 }}>Filtros</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs:12,sm:6,md:3}}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Colaborador</InputLabel>
                <Select value={filtroColaborador} label="Colaborador" onChange={(e) => setFiltroColaborador(e.target.value as number)}>
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {colaboradores.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs:6,md:3 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Ramo</InputLabel>
                <Select value={filtroRamo} label="Ramo" onChange={(e) => setFiltroRamo(e.target.value as number)}>
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {ramos.map((r) => (<MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs:6,md:3 }}  >
              <TextField fullWidth variant="outlined" label="Ano" value={filtroAno} onChange={(e) => setFiltroAno(e.target.value)} />
            </Grid>
            <Grid size={{ xs:6,md:3 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Mês</InputLabel>
                <Select value={filtroMes} label="Mês" onChange={(e) => setFiltroMes(e.target.value as MesCompetencia | '')}>
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {Object.values(MesCompetencia).map((mes) => (<MenuItem key={mes} value={mes}>{mes}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Seção de Resultados */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, position: 'relative' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Resultados</Typography>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Colaborador</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Ano</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Mês</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Ramo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Valor da Meta</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} align="center"><CircularProgress /></TableCell></TableRow>
                ) : metas.length > 0 ? (
                  metas.map((meta) => (
                    <TableRow key={meta.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ textTransform: 'uppercase' }}>{meta.colaborador.nome}</TableCell>
                      <TableCell sx={{ textTransform: 'uppercase' }}>{meta.ano}</TableCell>
                      <TableCell sx={{ textTransform: 'uppercase' }}>{meta.mes}</TableCell>
                      <TableCell sx={{ textTransform: 'uppercase' }}>{meta.ramo.nome}</TableCell>
                      <TableCell>{formatarMoeda(meta.valorMeta)}</TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleOpenDialog(meta)}><EditIcon /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(meta.id)}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow><TableCell colSpan={6} align="center">Nenhuma meta encontrada para os filtros selecionados.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth component="form" onSubmit={handleSubmitForm(onSubmit)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{metaAtual ? 'Editar Meta' : 'Criar Nova Meta'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs:12}}>
              <Controller
                name="colaboradorId"
                control={control}
                rules={{ required: 'Colaborador é obrigatório', min: { value: 1, message: 'Selecione um colaborador' } }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.colaboradorId} disabled={!!metaAtual}>
                    <InputLabel>Colaborador *</InputLabel>
                    <Select {...field} label="Colaborador *">
                      <MenuItem value={0}><em>Selecione um colaborador</em></MenuItem>
                      {colaboradores.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs:12}}>
              <Controller
                name="ramoId"
                control={control}
                rules={{ required: 'Ramo é obrigatório', min: { value: 1, message: 'Selecione um ramo' } }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.ramoId} disabled={!!metaAtual}>
                    <InputLabel>Ramo *</InputLabel>
                    <Select {...field} label="Ramo *">
                      <MenuItem value={0}><em>Selecione um ramo</em></MenuItem>
                      {ramos.map((r) => (<MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs:6}}>
              <TextField
                fullWidth
                label="Ano"
                {...register('ano', { required: 'Ano é obrigatório' })}
                error={!!errors.ano}
                helperText={errors.ano?.message}
                disabled={!!metaAtual}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="mes"
                control={control}
                rules={{ required: 'Mês é obrigatório' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.mes} disabled={!!metaAtual}>
                    <InputLabel>Mês *</InputLabel>
                    <Select {...field} label="Mês *">
                      {Object.values(MesCompetencia).map((mes) => (<MenuItem key={mes} value={mes}>{mes}</MenuItem>))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{xs:12}}>
              <TextField
                fullWidth
                label="Valor da Meta"
                type="number"
                {...register('valorMeta', {
                  required: 'Valor da meta é obrigatório',
                  valueAsNumber: true,
                  min: { value: 0.01, message: 'O valor deve ser maior que zero' }
                })}
                error={!!errors.valorMeta}
                helperText={errors.valorMeta?.message}
                inputProps={{
                  step: "0.01" // Permite a inserção de valores decimais (centavos)
                }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button type="submit" variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default GerenciarMetas;